import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Box, Pagination, formattedCurrency, usePagination, withStyles } from 'vopay';
import { SideDrawer, Skeleton, Table as TableUI } from '#shared/components/UI';
import useDirectSubAccounts from '#shared/hooks/useApi/useDirectSubAccounts';
import { useToast } from '#shared/providers/Toast';
import useSubAccountList from './useSubAccountList';
import SubAccountSearchForm, { SubAccountStatus } from './SubAccountSearchForm';
import SubAccountStatusBadge from './SubAccountStatusBadge';
import Table from './Table/Table';
import { mapBalances } from './subAccountRows';
import styles from './SubAccountsDrawer.module.scss';

const cn = withStyles(styles);

const PAGE_SIZE = 10;

type SubAccountsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The sub-account whose sub-accounts are listed, shown under the title. */
  accountName: string;
  /** Its JWT (the list row's Token), which lists its sub-accounts. */
  token: string;
  /** The list's status filter, which the drawer starts on so its rows match the count that opened it. */
  initialStatus: SubAccountStatus;
};

// Read-only list of one sub-account's direct sub-accounts, opened from the list's Sub-Accounts count (ID-13661).
// Fetches GET /account/subaccount?DirectOnly=true with the sub-account's own JWT, so the backend filters and pages its direct sub-accounts
// only (ID-13660). Its filters are its own: they never touch the list's URL.
export default function SubAccountsDrawer({
  isOpen,
  onClose,
  accountName,
  token,
  initialStatus,
}: SubAccountsDrawerProps) {
  const { hostPaths } = useSubAccountList();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList' });
  const toast = useToast();
  const { directSubAccounts, pagination, fetchingDirectSubAccounts, fetchDirectSubAccounts } = useDirectSubAccounts();

  const [filters, setFilters] = useState<{
    searchTerm: string;
    status: SubAccountStatus;
    currency: string;
    country: string;
  }>({ searchTerm: '', status: initialStatus, currency: '', country: '' });

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(pagination.TotalRecords, {
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchDirectSubAccounts(
          {
            SearchText: filters.searchTerm || undefined,
            ActiveOnly: filters.status === 'Active' ? true : filters.status === 'NotActive' ? false : undefined,
            Currency: filters.currency || undefined,
            Country: filters.country || undefined,
            Limit: PAGE_SIZE,
            Offset: offset,
          },
          token,
        );
      } catch (error) {
        // The hook already logged.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('SubAccountsDrawer.loadError', {
            errorMessage,
            defaultValue: 'Could not load the sub-accounts: {{errorMessage}}',
          }),
        );
      }
    };

    fetchData();
  }, [filters, offset, token]);

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClassName={cn('drawer')}
      title={
        <div className={cn('header')}>
          <span>{t('SubAccountsDrawer.title', { defaultValue: 'Sub-Accounts' })}</span>

          <BodyText weight="md" className={cn('accountName')}>
            {accountName}
          </BodyText>
        </div>
      }
    >
      <div className={cn('content')}>
        <SubAccountSearchForm
          status={filters.status}
          searchTerm={filters.searchTerm}
          currency={filters.currency}
          country={filters.country}
          fieldNamePrefix="subAccountsDrawer-"
          search={(newFilters) => {
            setPageIndex(0);
            setFilters(newFilters);
          }}
        />

        {fetchingDirectSubAccounts ? (
          <Skeleton height="320px" />
        ) : (
          <Table
            id="subAccountsDrawerList"
            hasPlaceholderArrows={false}
            rows={mapBalances(directSubAccounts).map((row) => ({
              id: `${row.AccountID}-${row.Currency}`,
              data: row,
            }))}
            columns={[
              {
                id: 'accountName',
                header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
                align: 'left',
                template: ({ data }) => (
                  <TableUI.Link to={hostPaths.subAccountDetail(data.AccountID, data.Currency)}>
                    {data.AccountName}
                  </TableUI.Link>
                ),
              },
              {
                id: 'status',
                header: t('Attributes.status', { defaultValue: 'Status' }),
                align: 'left',
                template: ({ data }) => <SubAccountStatusBadge subAccount={data} />,
              },
              {
                id: 'country',
                header: t('Attributes.country', { defaultValue: 'Country' }),
                field: 'Country',
                align: 'left',
              },
              {
                id: 'currency',
                header: t('Attributes.currency', { defaultValue: 'Currency' }),
                field: 'Currency',
                align: 'left',
              },
              {
                id: 'availableBalance',
                header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
                align: 'left',
                template: ({ data }) => `${data.Currency} $${formattedCurrency(data.AvailableBalance)}`,
              },
            ]}
          />
        )}

        {pageCount > 1 && (
          <Box flx="row" jc="center">
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => setPageIndex(e.selected)}
            />
          </Box>
        )}
      </div>
    </SideDrawer>
  );
}
